import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SyncIcon from '@mui/icons-material/Sync';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';

const ShiftHeader = ({
  shift,
  onReset,
  onDeleteShift,
  onChangeGuard,
  onSyncToCalendar,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSyncToCalendar = () => {
    handleMenuClose();
    onSyncToCalendar?.();
  };

  const handleChangeGuard = () => {
    handleMenuClose();
    onChangeGuard?.();
  };

  const handleDeleteShift = () => {
    handleMenuClose();
    onDeleteShift?.();
  };

  const formatShiftTimeRange = () => {
    if (!shift) return '';
    const start = moment(shift.StartTime).format('MMM D, h:mm A');
    const end = moment(shift.EndTime).format('MMM D, h:mm A');
    return `${start} - ${end}`;
  };

  const formatDuration = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    }
    return `${hours.toFixed(1)} hrs`;
  };

  return (
    <>
      {/* Shift Header with Reset Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
            Shift View
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Viewing shift for{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {shift.ContactName}
            </Box>
            {' '}on{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {moment(shift.StartTime).isSame(moment(shift.EndTime), 'day')
                ? moment(shift.StartTime).format('ddd, MMM D, YYYY')
                : `${moment(shift.StartTime).format('ddd, MMM D')} to ${moment(shift.EndTime).format('ddd, MMM D, YYYY')}`}
            </Box>
          </Typography>
        </Box>
        <Tooltip title="Clear shift view">
          <IconButton
            size="small"
            onClick={onReset}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Shift Metadata */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: 0.5 }}>
          {formatShiftTimeRange()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, marginTop: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={<AccessTimeIcon />}
            label={formatDuration(shift.totalDuration)}
            size="small"
            variant="outlined"
            color="primary"
          />
          <Chip
            icon={<WorkIcon />}
            label={`${shift.patrolCount} patrol${shift.patrolCount > 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
          {shift.locationCount > 1 && (
            <Chip
              icon={<LocationOnIcon />}
              label={`${shift.locationCount} locations`}
              size="small"
              variant="outlined"
            />
          )}
          {/* Actions Menu */}
          <Tooltip title="Shift actions">
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ ml: 'auto' }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {onSyncToCalendar && (
              <MenuItem onClick={handleSyncToCalendar}>
                <ListItemIcon>
                  <SyncIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sync to Calendar</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleChangeGuard}>
              <ListItemIcon>
                <SwapHorizIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Change Guard</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleDeleteShift} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Shift</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </>
  );
};

ShiftHeader.propTypes = {
  shift: PropTypes.shape({
    ContactName: PropTypes.string,
    StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    totalDuration: PropTypes.number,
    patrolCount: PropTypes.number,
    locationCount: PropTypes.number,
  }).isRequired,
  onReset: PropTypes.func,
  onDeleteShift: PropTypes.func,
  onChangeGuard: PropTypes.func,
  onSyncToCalendar: PropTypes.func,
};

export default memo(ShiftHeader);
